import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Shield, TrendingUp, Lock, Users, FileText, CheckCircle, 
  ArrowRight, Building2, Coins, Globe, Mail, ExternalLink,
  BarChart3, Landmark, Award, Send, User, Phone, Briefcase
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Investors = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    investment_range: '',
    accredited: false,
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email) {
      toast.error('Please fill in required fields');
      return;
    }
    if (!formData.accredited) {
      toast.error('This opportunity is for accredited investors only');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/investor-inquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        toast.success('Inquiry submitted! We will contact you shortly.');
        setSubmitted(true);
        setFormData({
          name: '', email: '', phone: '', company: '',
          investment_range: '', accredited: false, message: ''
        });
      } else {
        toast.error('Failed to submit inquiry. Please try again.');
      }
    } catch (error) {
      toast.error('Error submitting inquiry');
    } finally {
      setSubmitting(false);
    }
  };

  const KEY_METRICS = [
    { label: 'Target Raise', value: '$5M', sublabel: 'Seed Round' },
    { label: 'Token Supply', value: '10B', sublabel: 'BETH Tokens' },
    { label: 'Network', value: 'Ethereum', sublabel: 'Mainnet' },
    { label: 'Structure', value: 'Reg D 506(b)', sublabel: 'Accredited Only' }
  ];

  const USE_OF_FUNDS = [
    { category: 'Technology & Development', percentage: 40 },
    { category: 'Liquidity & Treasury', percentage: 25 },
    { category: 'Compliance & Legal', percentage: 15 },
    { category: 'Marketing & BD', percentage: 12 },
    { category: 'Operations', percentage: 8 }
  ];

  const COMPETITIVE_ADVANTAGES = [
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'Compliance-First Architecture',
      description: 'Built for Reg D 506(b) from day one. Accredited investor verification, KYC/AML ready.'
    },
    {
      icon: <Lock className="w-6 h-6" />,
      title: 'Non-Custodial Design',
      description: 'Smart contract transparency. Users maintain control of their assets at all times.'
    },
    {
      icon: <Landmark className="w-6 h-6" />,
      title: 'Institutional-Grade UX',
      description: 'Private bank experience. Not another DeFi dashboard - designed for serious capital.'
    },
    {
      icon: <Coins className="w-6 h-6" />,
      title: 'Real Yield Model',
      description: 'Credit facility interest, not inflationary rewards. Sustainable tokenomics.'
    }
  ];

  const TEAM_HIGHLIGHTS = [
    'Founders with 15+ years combined finance & blockchain experience',
    'Advisors from traditional private credit & institutional finance',
    'Smart contracts audited and deployed on Ethereum Mainnet',
    'Legal structure reviewed by securities counsel'
  ];

  const DEPLOYED_CONTRACTS = [
    { name: 'BETH Utility Token', address: '0x6822824c5349bf005e353ec5dda3fa34595ea811' },
    { name: 'Collateral Vault', address: '0xe86c1f0fd87165cbde68714e6f4cb152e7e5707d' },
    { name: 'Credit Line Contract', address: '0x8abfe72db538c6ff99452a910b9bc7a980ef0fdd' },
    { name: 'Treasury Manager', address: '0xc21cd1629a88fe99dbf4cf5d202c04bd493cbf2c' }
  ];

  return (
    <div className="min-h-screen bg-[#0a1628]">
      {/* Hero Section */}
      <header className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#b8860b]/10 to-transparent" />
        <nav className="relative z-10 max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#b8860b] rounded-lg flex items-center justify-center text-white font-bold">B</div>
            <div>
              <span className="text-white font-bold text-xl tracking-wide">BETHEREUM</span>
              <p className="text-[#b8860b] text-[10px] tracking-[0.2em]">PRIVATE WEALTH MANAGEMENT</p>
            </div>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/compliance">
              <Button variant="ghost" className="text-gray-400 hover:text-white">Compliance</Button>
            </Link>
            <Link to="/login">
              <Button className="bg-[#b8860b] hover:bg-[#9a7209]">Platform Login</Button>
            </Link>
          </div>
        </nav>

        <div className="relative z-10 max-w-6xl mx-auto px-6 py-20 text-center">
          <p className="text-[#b8860b] tracking-[0.3em] text-sm mb-4">INVESTMENT OPPORTUNITY</p>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Institutional Private Credit
            <br />
            <span className="text-[#b8860b]">On-Chain</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-8">
            A permissioned digital credit infrastructure for accredited participants. 
            Smart contract transparency meets institutional finance.
          </p>
          <div className="flex gap-4 justify-center">
            <a href="mailto:invest@bethereum.com">
              <Button size="lg" className="bg-[#b8860b] hover:bg-[#9a7209]">
                <Mail className="w-5 h-5 mr-2" />
                Request Investor Deck
              </Button>
            </a>
            <a href="#overview">
              <Button size="lg" variant="outline" className="border-[#b8860b] text-[#b8860b] hover:bg-[#b8860b]/10">
                Learn More
              </Button>
            </a>
          </div>
        </div>
      </header>

      {/* Key Metrics */}
      <section className="py-16 border-y border-[#1a2d4a]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {KEY_METRICS.map((metric, idx) => (
              <div key={idx} className="text-center">
                <p className="text-3xl md:text-4xl font-bold text-white">{metric.value}</p>
                <p className="text-[#b8860b] font-medium">{metric.label}</p>
                <p className="text-gray-500 text-sm">{metric.sublabel}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Problem & Solution */}
      <section id="overview" className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                <span className="w-8 h-8 bg-red-500/20 rounded-full flex items-center justify-center text-red-400">!</span>
                The Problem
              </h2>
              <ul className="space-y-4 text-gray-400">
                <li className="flex items-start gap-3">
                  <span className="text-red-400 mt-1">•</span>
                  <span>Traditional private credit is opaque, illiquid, and inaccessible to most accredited investors</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-400 mt-1">•</span>
                  <span>DeFi lending protocols lack compliance frameworks and institutional safeguards</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-400 mt-1">•</span>
                  <span>$1.5T private credit market with no compliant on-chain infrastructure</span>
                </li>
              </ul>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                <span className="w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center text-green-400">✓</span>
                Our Solution
              </h2>
              <ul className="space-y-4 text-gray-400">
                <li className="flex items-start gap-3">
                  <span className="text-green-400 mt-1">•</span>
                  <span>Permissioned credit infrastructure with smart contract transparency</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-400 mt-1">•</span>
                  <span>Reg D 506(b) compliant from architecture level - accredited investors only</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-400 mt-1">•</span>
                  <span>BETH utility token for collateral, with institutional-grade admin controls</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Competitive Advantages */}
      <section className="py-20 bg-[#0d1d35]">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-white text-center mb-12">Why Bethereum</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {COMPETITIVE_ADVANTAGES.map((adv, idx) => (
              <Card key={idx} className="bg-[#0a1628] border-[#1a2d4a]">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#b8860b]/20 rounded-lg flex items-center justify-center text-[#b8860b]">
                      {adv.icon}
                    </div>
                    <div>
                      <h3 className="text-white font-bold mb-2">{adv.title}</h3>
                      <p className="text-gray-400 text-sm">{adv.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Use of Funds */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-white text-center mb-12">Use of Funds</h2>
          <div className="max-w-2xl mx-auto space-y-4">
            {USE_OF_FUNDS.map((item, idx) => (
              <div key={idx}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-400">{item.category}</span>
                  <span className="text-white font-bold">{item.percentage}%</span>
                </div>
                <div className="h-3 bg-[#1a2d4a] rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-[#b8860b] to-[#d4a00a] rounded-full"
                    style={{ width: `${item.percentage}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Deployed Contracts */}
      <section className="py-20 bg-[#0d1d35]">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-white text-center mb-4">Live on Ethereum Mainnet</h2>
          <p className="text-gray-400 text-center mb-12">Smart contracts deployed and verified on Etherscan</p>
          <div className="grid md:grid-cols-2 gap-4 max-w-4xl mx-auto">
            {DEPLOYED_CONTRACTS.map((contract, idx) => (
              <a 
                key={idx}
                href={`https://etherscan.io/address/${contract.address}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-4 bg-[#0a1628] border border-[#1a2d4a] rounded-lg hover:border-[#b8860b]/50 transition-colors group"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-medium">{contract.name}</p>
                    <p className="text-gray-500 text-xs font-mono">{contract.address.slice(0, 16)}...</p>
                  </div>
                  <ExternalLink className="w-4 h-4 text-gray-500 group-hover:text-[#b8860b]" />
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-white text-center mb-12">Team & Credentials</h2>
          <div className="max-w-2xl mx-auto">
            <div className="space-y-4">
              {TEAM_HIGHLIGHTS.map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 p-4 bg-[#0d1d35] rounded-lg border border-[#1a2d4a]">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span className="text-gray-300">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Investor Inquiry Form */}
      <section id="inquiry-form" className="py-20 bg-[#0d1d35]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-[#b8860b] tracking-[0.3em] text-sm mb-4">GET IN TOUCH</p>
            <h2 className="text-3xl font-bold text-white mb-4">Investor Inquiry</h2>
            <p className="text-gray-400">
              Submit your information and our team will reach out with detailed investment materials.
            </p>
          </div>

          {submitted ? (
            <Card className="bg-[#1a2d4a] border-[#b8860b]/30 max-w-xl mx-auto">
              <CardContent className="py-12 text-center">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-green-400" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">Thank You!</h3>
                <p className="text-gray-400 mb-6">
                  Your inquiry has been submitted. A member of our investor relations team will contact you within 24-48 hours.
                </p>
                <Button 
                  onClick={() => setSubmitted(false)} 
                  variant="outline" 
                  className="border-[#b8860b] text-[#b8860b]"
                >
                  Submit Another Inquiry
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-[#1a2d4a] border-[#2a3d5a] max-w-2xl mx-auto">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-gray-300 flex items-center gap-2">
                        <User className="w-4 h-4" /> Full Name *
                      </Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        placeholder="John Smith"
                        required
                        className="bg-[#0d1d35] border-[#2a3d5a] text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-gray-300 flex items-center gap-2">
                        <Mail className="w-4 h-4" /> Email *
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        placeholder="john@company.com"
                        required
                        className="bg-[#0d1d35] border-[#2a3d5a] text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-gray-300 flex items-center gap-2">
                        <Phone className="w-4 h-4" /> Phone
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        placeholder="+1 (555) 123-4567"
                        className="bg-[#0d1d35] border-[#2a3d5a] text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company" className="text-gray-300 flex items-center gap-2">
                        <Briefcase className="w-4 h-4" /> Company / Fund
                      </Label>
                      <Input
                        id="company"
                        value={formData.company}
                        onChange={(e) => setFormData({...formData, company: e.target.value})}
                        placeholder="ABC Capital"
                        className="bg-[#0d1d35] border-[#2a3d5a] text-white"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-gray-300">Investment Range</Label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {['$25K - $100K', '$100K - $500K', '$500K - $1M', '$1M+'].map((range) => (
                        <button
                          key={range}
                          type="button"
                          onClick={() => setFormData({...formData, investment_range: range})}
                          className={`p-3 rounded border text-sm transition-colors ${
                            formData.investment_range === range
                              ? 'bg-[#b8860b] border-[#b8860b] text-white'
                              : 'bg-[#0d1d35] border-[#2a3d5a] text-gray-400 hover:border-[#b8860b]'
                          }`}
                        >
                          {range}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-gray-300">Message (Optional)</Label>
                    <textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      placeholder="Tell us about your investment interests..."
                      rows={3}
                      className="w-full p-3 rounded bg-[#0d1d35] border border-[#2a3d5a] text-white placeholder:text-gray-500 resize-none"
                    />
                  </div>

                  <div className="flex items-start gap-3 p-4 bg-[#0d1d35] rounded border border-[#2a3d5a]">
                    <input
                      type="checkbox"
                      id="accredited"
                      checked={formData.accredited}
                      onChange={(e) => setFormData({...formData, accredited: e.target.checked})}
                      className="mt-1 w-5 h-5 rounded border-[#2a3d5a] bg-[#1a2d4a] text-[#b8860b] focus:ring-[#b8860b]"
                    />
                    <label htmlFor="accredited" className="text-gray-300 text-sm">
                      I confirm that I am an <span className="text-[#b8860b] font-medium">accredited investor</span> as defined 
                      by SEC Rule 501 of Regulation D, and I understand this investment opportunity is only available to 
                      accredited investors. *
                    </label>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-[#b8860b] hover:bg-[#9a7209] text-white py-6"
                    disabled={submitting}
                  >
                    {submitting ? (
                      'Submitting...'
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Submit Inquiry
                      </>
                    )}
                  </Button>

                  <p className="text-gray-500 text-xs text-center">
                    By submitting, you agree to our <Link to="/privacy" className="text-[#b8860b] hover:underline">Privacy Policy</Link>. 
                    We will never share your information with third parties.
                  </p>
                </form>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-[#0d1d35] to-[#0a1628]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Learn More?</h2>
          <p className="text-gray-400 mb-8">
            Request our full investor deck with detailed financials, tokenomics, and roadmap.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="mailto:invest@bethereum.com?subject=Investor%20Deck%20Request">
              <Button size="lg" className="bg-[#b8860b] hover:bg-[#9a7209] w-full sm:w-auto">
                <Mail className="w-5 h-5 mr-2" />
                Request Investor Deck
              </Button>
            </a>
            <a href="#inquiry-form">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 w-full sm:w-auto">
                Fill Out Inquiry Form
              </Button>
            </a>
          </div>
          <p className="text-gray-500 text-sm mt-6">
            For accredited investors only. This is not a solicitation to purchase securities.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-[#1a2d4a]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-[#b8860b] rounded flex items-center justify-center text-white font-bold text-sm">B</div>
              <span className="text-gray-400">© 2026 Bethereum. All rights reserved.</span>
            </div>
            <div className="flex gap-6 text-sm">
              <Link to="/compliance" className="text-gray-400 hover:text-white">Compliance</Link>
              <Link to="/terms" className="text-gray-400 hover:text-white">Terms</Link>
              <Link to="/privacy" className="text-gray-400 hover:text-white">Privacy</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Investors;
